import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  animate,
} from "motion/react";
import Logo from "../../images/TrattoriaDemiCenteredWhite.png";
import { useMobile } from "../../context/MobileContext";
import { useSubNavSlot } from "./SubNavContext";
import "./nav.css";

export function Navbar() {
  const { mobile } = useMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSlot } = useSubNavSlot();
  const [currentPage, setCurrentPage] = useState("");
  const [showNav, setShowNav] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  /* The primary navbar and the sub-nav hide as one rigid stack: both
     translate by the same pixel value (stackY). The navbar gets it as a
     motion value; the sub-nav (portal slot) reads the same number via a
     body-level CSS variable (see nav.css). Travel distance = combined
     height of both bars, so the whole stack just clears the top. */
  const stackY = useMotionValue(0);
  const travelRef = useRef(160);
  const measureTravel = useCallback(() => {
    const nav = document.querySelector(".navbar");
    const sub = document.querySelector(".subnav");
    travelRef.current =
      (nav ? nav.offsetHeight : 80) + (sub ? sub.offsetHeight : 0) + 6;
  }, []);

  useMotionValueEvent(stackY, "change", (v) => {
    document.body.style.setProperty("--nav-stack-y", `${v}px`);
  });

  /* Desktop: scroll-linked — the stack slides away in lockstep with the
     scroll as the fancy line under the footer wordmark enters the
     viewport (by then the giant Trattoria Demi mark is on screen and
     the navbar would repeat it). */
  const wordmarkRef = useRef(null);
  useEffect(() => {
    wordmarkRef.current = document.querySelector(".sf-fancy-line");
  }, []);
  const { scrollYProgress: footerProgress } = useScroll({
    target: wordmarkRef,
    offset: ["start end", "end end"],
  });
  useMotionValueEvent(footerProgress, "change", (v) => {
    if (mobile) return;
    if (v > 0 && stackY.get() === 0) measureTravel();
    stackY.set(-v * travelRef.current);
  });

  /* Mobile: the stack stays stickied and only animates away once the
     footer has essentially filled the screen. */
  const reducedMotion = useReducedMotion();
  const [footerDominant, setFooterDominant] = useState(false);
  useEffect(() => {
    if (!mobile) {
      setFooterDominant(false);
      return;
    }
    const footer = document.querySelector(".site-footer");
    if (!footer || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setFooterDominant(entry.intersectionRatio >= 0.85),
      { threshold: [0, 0.85] }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, [mobile]);

  useEffect(() => {
    if (!mobile) return;
    measureTravel();
    const controls = animate(
      stackY,
      footerDominant ? -travelRef.current : 0,
      {
        duration: reducedMotion ? 0 : 0.45,
        ease: [0.22, 1, 0.36, 1],
      }
    );
    return () => controls.stop();
  }, [mobile, footerDominant, reducedMotion, stackY, measureTravel]);

  const navHide = { style: { y: stackY, willChange: "transform" } };

  const determineLocation = useCallback((path) => {
    let loc = path.substring(1);
    if (loc === "checkout") loc = "order";
    if (loc === "reserve/privacy-policy") loc = "reserve";
    if (loc === "reserve/terms-of-service") loc = "reserve";
    if (loc === "") loc = "home";
    if (loc === "dashboard") loc = "home";
    if (loc === "giftcards/") loc = "giftcards";
    if (loc === "email") loc = "home";
    if (loc === "signout") loc = "home";
    if (loc.match(/^order-status\/.*/)) loc = "home";
    if (loc.match(/^cancel\/.*/)) loc = "home";
    return loc;
  }, []);

  useEffect(() => {
    const loc = determineLocation(location.pathname);
    setCurrentPage(loc);
  }, [location.pathname, determineLocation]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const onDocMouseDown = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const navHome = () => {
    navigate("/home");
  };

  const clickHandler = (event) => {
    const newPage = event.currentTarget.id;
    if (mobile) setShowNav(false);
    setMoreOpen(false);
    setCurrentPage(newPage);
    navigate("/" + newPage);
  };

  const renderNavLinks = (links) => {
    return links.map((link) => (
      <div
        key={link}
        id={link}
        className={`navbar-link ${
          currentPage === link ? "navbar-link-active" : ""
        }`}
        onClick={clickHandler}
      >
        {link.charAt(0).toUpperCase() + link.slice(1)}
      </div>
    ));
  };

  const leftLinks = ["menu", "reserve"];
  const rightLinks = ["order"];
  const moreLinks = ["contact", "deals", "gallery", "giftcards", "FAQ"];
  const mobileLinks = [...leftLinks, ...rightLinks, ...moreLinks];
  const isMoreActive = moreLinks.includes(currentPage);

  const primaryNav = mobile ? (
    <motion.div className="navbar navbar-mobile" {...navHide}>
      <img
        className="navbar-logo-mobile"
        onClick={navHome}
        src={Logo}
        alt="Trattoria Demi"
      />
      <button
        type="button"
        className="hamburger-button"
        onClick={() => setShowNav(!showNav)}
      >
        <div className={`hamburger-line ${showNav && "ham-line-1"}`} />
        <div className={`hamburger-line ${showNav && "ham-line-2"}`} />
        <div className={`hamburger-line ${showNav && "ham-line-3"}`} />
      </button>
      <AnimatePresence>
        {showNav && (
          <motion.div
            key="mobile-nav-scrim"
            className="mobile-nav-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => setShowNav(false)}
          />
        )}
      </AnimatePresence>
      <div className={`mobile-nav ${showNav && "mobile-nav-active"}`}>
        {renderNavLinks(mobileLinks)}
      </div>
    </motion.div>
  ) : (
    <motion.div className="navbar" {...navHide}>
      <div className="navbar-links">
        {renderNavLinks(leftLinks)}
        <img
          className="navbar-logo"
          onClick={navHome}
          src={Logo}
          alt="Trattoria Demi"
        />
        {renderNavLinks(rightLinks)}
        <div className="navbar-more" ref={moreRef}>
          <div
            className={`navbar-link navbar-more-toggle ${
              isMoreActive ? "navbar-link-active" : ""
            } ${moreOpen ? "navbar-more-toggle-open" : ""}`}
            onClick={() => setMoreOpen((o) => !o)}
          >
            More
            <span className="navbar-more-caret" aria-hidden="true">
              ▾
            </span>
          </div>
          <AnimatePresence>
            {moreOpen && (
              <motion.div
                className="navbar-dropdown"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {moreLinks.map((link) => (
                  <div
                    key={link}
                    id={link}
                    className={`navbar-dropdown-item ${
                      currentPage === link
                        ? "navbar-dropdown-item-active"
                        : ""
                    }`}
                    onClick={clickHandler}
                  >
                    {link.charAt(0).toUpperCase() + link.slice(1)}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );

  // The secondary nav (if any page registered one) portals into this slot, so
  // primary + secondary render as one sticky stack in the layout.
  return (
    <>
      {primaryNav}
      <div className="subnav-host" ref={setSlot} />
    </>
  );
}

export default Navbar;
